import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { applyDirection, type Language } from '../i18n.js';
import { useAuth } from '../lib/auth.js';

export function LoginPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggleLanguage = async (): Promise<void> => {
    const next: Language = i18n.language === 'ar' ? 'en' : 'ar';
    await i18n.changeLanguage(next);
    applyDirection(next);
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch {
      setError(t('login.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page" style={{ maxWidth: 420, paddingTop: 48 }}>
      <div className="spread" style={{ marginBottom: 24 }}>
        <div className="brand">
          SL <span>Meetings</span>
        </div>
        <button type="button" className="link" onClick={() => void toggleLanguage()}>
          {t('common.language')}
        </button>
      </div>

      <h1>{t('login.title')}</h1>
      <p className="muted" style={{ marginBottom: 20 }}>
        {t('login.subtitle')}
      </p>

      <form onSubmit={(e) => void onSubmit(e)} className="card">
        <div className="field">
          <label htmlFor="email">{t('login.email')}</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            dir="ltr"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="password">{t('login.password')}</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            dir="ltr"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
        <button type="submit" className="gold" disabled={busy} style={{ width: '100%' }}>
          {busy ? t('common.loading') : t('login.submit')}
        </button>
      </form>
    </div>
  );
}
